import Vue from 'vue'
import { useEventBus } from '@vueuse/core'
import { useGlobalConfigStore } from '@ms/stores/globalConfig'
import { turnOutcomesToMap } from '@ms/utils/eventHandlers/convert'
import parsePushData from 'custom-push/pushParser/parsePushData'
import parseTopicInfo from 'custom-push/pushParser/parseTopicInfo'
import { useExpandedMarkets } from 'composables/useExpandedMarkets.js'

// market 是否应该聚合，true - 应该，false - 不用聚合
const shouldMarketAggregate = (presentationType) => {
  return [1, 7, 9, 10, 11].includes(presentationType)
}

/**
 * 更新详情页 market status
 * 如果当前 market status === 2 或者 3，则不更新
 * @param {*} markets {
 *  id: market,
 *  id: [market] 带specifiers
 * }
 */
export function updateMarketStatus(markets, status) {
  if (status !== 1 && status !== 2) {
    return
  }
  if (markets.order) {
    useEventBus('updateMarketStatus').emit(status)
    markets.order.forEach((id) => {
      const m = markets.map[id]
      if (Array.isArray(m)) {
        m.forEach((market) => {
          if (market.status === 0 || market.status === 1) {
            market.status = status
          }
        })
      } else {
        if (m.status === 0 || m.status === 1) {
          m.status = status
        }
      }
    })
  }
}

// 由于 market 分组和展示需要根据 presentationType 来判断，所以在 turnMarketsToMap 转化得到的 map.id 不一定等于 market.id
export const getMarketSymbolId = (market = {}) => {
  const { specifiers, presentationType, id } = market
  let key = id
  if (specifiers && !shouldMarketAggregate(presentationType)) {
    key = `${id}_${specifiers}`
  }
  return key
}

/**
 * 将 markets array 转变成 map
 * @param  {Array}   markets  markets list
 * @return {Object}          { order: [[marketId]], map: {} }, order 记录了原始列表顺序，map 是 market 数据对象
 */
export const turnMarketsToMap = (markets = []) => {
  const order = []
  const map = {}
  markets.forEach((market) => {
    const { presentationType, id, specifiers, outcomes, ...rest } = market

    const key = getMarketSymbolId(market)

    if (!order.includes(key)) {
      order.push(key)
    }

    const result = {
      key,
      id,
      specifiers,
      presentationType,
      outcomes: turnOutcomesToMap(outcomes),
      ...rest
    }

    if (specifiers && shouldMarketAggregate(presentationType)) {
      if (!map[key]) {
        map[key] = []
      }
      map[key].push(result)
    } else {
      map[key] = result
    }
  })
  return { order, map }
}

/**
 * 将 markets array 转变成 map
 * @param  {Array}   groups  marketGroups list
 * @return {Object}          { order: [[marketId]], map: {} }, order 记录了原始列表顺序，map 是 market 数据对象
 */
export const turnMarketCombosToMap = (groups = []) => {
  const order = []
  const map = {}
  groups.forEach((group) => {
    const { comboGroupName, comboType, marketGroups, isExpand } = group

    let key = `${comboType}_${comboGroupName}`
    marketGroups.forEach((marketGroup) => {
      if (!marketGroup.markets.length) return false
      const { marketTypeDesc, markets } = marketGroup
      markets.forEach((market) => {
        const { presentationType, id, specifiers, outcomes, ...rest } = market

        if (comboType === -1) {
          key = getMarketSymbolId(market)
        } else if (comboType !== 1) {
          key = `${comboType}_${comboGroupName}_${id}`
        }

        const expand = {}
        if (comboType !== -1) {
          expand.isExpand = isExpand
        }

        if (!order.includes(key)) {
          order.push(key)
        }

        const result = {
          key,
          id,
          specifiers,
          presentationType,
          comboType,
          groupName: comboGroupName,
          marketTypeDesc,
          outcomes: turnOutcomesToMap(outcomes),
          ...rest,
          ...expand
        }

        if (comboType === 1 || (specifiers && shouldMarketAggregate(presentationType))) {
          if (!map[key]) {
            map[key] = []
          }
          map[key].push(result)
        } else {
          map[key] = result
        }
      })
    })
  })
  return { order, map }
}

/**
 * 根据推送消息，更新对应 event market 信息
 * @param  {Object} event    显示的 event 比赛对象，注意其中的 markets 信息有做 convert
 * @param  {Object} pushData 推送消息对象
 * @return {[type]}          [description]
 */
export const updateMarket = (event, pushData) => {
  const pushMarketInfo = parsePushData(pushData, 'market')
  const topicInfo = parseTopicInfo(pushData[0], 'market')

  const { marketId } = topicInfo

  let specifiers = topicInfo.marketSpecifiers
  if (specifiers) {
    // topic中不能出现 |所以specifier多个的情况用&连接，现在在改回去
    specifiers = specifiers.split('&').join('|')
  }

  // 新的market
  let market = { id: marketId }
  if (specifiers) {
    market.specifiers = specifiers
  }

  if (!event.markets || !event.markets.map) {
    event.markets = { map: {}, order: [] }
  }

  const id = getMarketSymbolId({ id: marketId, specifiers, ...pushMarketInfo })
  const originMarket = event.markets.map[id]
  const oldTime = originMarket ? +originMarket.pushTime || 0 : 0

  if (originMarket) {
    // 如果已经是 live 了，就不合并推送的 prematch 信息
    if (+pushMarketInfo.product === 3 && originMarket.product === 1) {
      return
    }

    market = {
      ...market,
      ...pushMarketInfo
    }

    let customMarket = originMarket

    if (specifiers) {
      if (Array.isArray(originMarket)) {
        const i = originMarket.findIndex((cur) => cur.specifiers === specifiers)

        if (i > -1) {
          market.outcomes = originMarket[i].outcomes
          originMarket.splice(i, 1, market)
        } else {
          originMarket.push(market)
        }
        customMarket = market
      } else {
        Object.assign(originMarket, pushMarketInfo)
        market = originMarket
      }
    } else {
      Object.assign(originMarket, pushMarketInfo)
      market = originMarket
    }
    useEventBus('updateMarket').emit(customMarket, pushMarketInfo)

    // Update expanded market
    const { trackMarketExpandStatus } = useExpandedMarkets()

    trackMarketExpandStatus({
      marketGroup: market.group,
      marketId: isNaN(Number(id)) ? id : Number(id),
      status: market.status
    })
  } else if (oldTime < +pushMarketInfo.pushTime) {
    // 新增 market
    const reg = new RegExp(`^${marketId}_`)
    const order = event.markets.order
    const index = order
      .slice()
      .reverse()
      .findIndex((x) => reg.test(x) || `${x}` === `${marketId}`)
    const count = order.length - 1
    const finalIndex = index >= 0 ? count - index : index
    if (finalIndex !== -1) {
      event.markets.order.splice(finalIndex + 1, 0, id)
    } else {
      event.markets.order.push(id)
    }
    market = { ...market, ...pushMarketInfo }
    const { igopmsMarkets } = useGlobalConfigStore()
    if (!('expand' in market)) {
      if (igopmsMarkets.includes(market.id)) {
        // 默认收起
        market.expand = 0
      } else {
        // 新增 market 默认展开
        market.expand = 1
      }
    }
    if (specifiers && shouldMarketAggregate(+pushMarketInfo.presentationType)) {
      Vue.set(event.markets.map, id, [market])
    } else {
      Vue.set(event.markets.map, id, market)
    }

    const { trackMarketExpandStatus } = useExpandedMarkets()

    trackMarketExpandStatus({
      marketGroup: market.group,
      marketId: isNaN(Number(id)) ? id : Number(id),
      expand: market.expand,
      status: market.status
    })
  }

  return { market, oldTime, pushMarketInfo }
}

/**
 * 根据推送消息，更新对应 event outcome 信息
 * @param  {Object} event    显示的 event 比赛对象，注意其中的 markets 信息有做 convert
 * @param  {Object} pushData 推送消息对象
 * @return {[type]}          [description]
 */
export const updateOutcome = (event, pushData) => {
  const pushOutcomeInfos = parsePushData(pushData, 'odds') || {}
  // 修正 odds 推送中的 market priority 位置
  const marketPushData = [...pushData]
  marketPushData[9] = marketPushData[10]
  const marketInfo = updateMarket(event, marketPushData)
  if (!marketInfo) return
  const { market, oldTime, pushMarketInfo } = marketInfo

  if (pushOutcomeInfos && oldTime <= +pushMarketInfo.pushTime) {
    let outcomes = market.outcomes
    if (!outcomes) {
      outcomes = { map: {}, order: [] }
      Vue.set(market, 'outcomes', outcomes)
    }

    Object.entries(pushOutcomeInfos).forEach(([id, outcome]) => {
      outcome.id = id
      if (outcomes.map[id]) {
        useEventBus('updateOutcome').emit(marketInfo, outcome)
        Object.assign(outcomes.map[id], outcome)
      } else {
        market.outcomes.order.push(id)
        Vue.set(market.outcomes.map, id, outcome)
      }
    })
  }
}
